import { useEffect, useState, useCallback } from "react";
import {
  getAllScoreSubmissions,
  approveScoreSubmission,
  managerApproveScoreSubmission,
} from "../../actions/studensts";
import { excelScoreSubmissions } from "../exportToExcel";
import Loading from "../../components/Loading";
import UNAUTHORIZED from "../UNAUTHORIZED";

// Step 1: these usernames can do the first-level review/approve.
const APPROVERS = ["asmaa"];
// Step 2: only this role can do the final manager approval.
const MANAGER_ROLE = "super-admin";
// View-only: can see the list and statuses, no approve buttons.
const VIEWERS = ["abdelrhman"];

const STATUS_STYLES = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const fmtDateTime = (val) => {
  if (!val) return "";
  const d = new Date(String(val).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function SendScoreSubmissions() {
  const profile = JSON.parse(localStorage.getItem("profile"))?.value?.[0];
  const username = profile?.username;
  const role = profile?.role;
  const isApprover = APPROVERS.includes(username);
  const isManager = role === MANAGER_ROLE;
  const isViewer = VIEWERS.includes(username);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [approvingId, setApprovingId] = useState(null);
  const [managerApprovingId, setManagerApprovingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");

  const load = useCallback(async () => {
    const res = await getAllScoreSubmissions();
    if (res?.status === 200) {
      setRows(res.data.data);
    } else {
      setError("Failed to load submissions. Please try again.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isApprover || isManager || isViewer) load();
  }, [load, isApprover, isManager, isViewer]);

  const handleApprove = async (id) => {
    setApprovingId(id);
    setError("");
    const res = await approveScoreSubmission(id);
    setApprovingId(null);
    if (res?.status === 200) {
      // Status stays "pending" until the manager approval step below runs.
      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, asmaa_approved: 1, asmaa_approved_by: username }
            : r
        )
      );
    } else {
      setError(res?.data?.ERROR || "Approve failed. Please try again.");
    }
  };

  const handleManagerApprove = async (id) => {
    setManagerApprovingId(id);
    setError("");
    const res = await managerApproveScoreSubmission(id);
    setManagerApprovingId(null);
    if (res?.status === 200) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, status: "approved", manager_approved_by: username }
            : r
        )
      );
    } else {
      setError(res?.data?.ERROR || "Manager approval failed. Please try again.");
    }
  };

  if (!isApprover && !isManager && !isViewer) {
    return <UNAUTHORIZED />;
  }

  const pendingCount = rows.filter((r) => r.status === "pending").length;

  // Filters by the submitted_at date (not time), so "To" is inclusive of
  // that whole day. Either bound can be left blank for an open-ended range;
  // leaving both blank exports every row currently listed.
  const handleExport = async () => {
    setExporting(true);
    const from = exportDateFrom;
    const to = exportDateTo;
    const filtered =
      from || to
        ? rows.filter((r) => {
            const day = String(r.submitted_at || "").slice(0, 10);
            return (!from || day >= from) && (!to || day <= to);
          })
        : rows;
    await excelScoreSubmissions(filtered, from || null, to || null);
    setExporting(false);
    setShowExportModal(false);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl text-white">Send Score Submissions</h1>
        <div className="flex items-center gap-3">
          <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full">
            {pendingCount} pending
          </span>
          <button
            onClick={() => setShowExportModal(true)}
            disabled={loading || rows.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded disabled:opacity-50"
          >
            Export to Excel
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {error && (
          <div className="px-6 py-3 bg-red-50 text-red-700 text-sm">{error}</div>
        )}
        {loading ? (
          <div className="flex justify-center py-10">
            <Loading />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-gray-500 text-center py-10">No submissions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-2">Full Name</th>
                  <th className="text-left px-4 py-2">National ID</th>
                  <th className="text-left px-4 py-2">ACT ID</th>
                  <th className="text-left px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Submitted At</th>
                  <th className="text-left px-4 py-2">Submitted By</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Asmaa Review</th>
                  <th className="text-center px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t odd:bg-gray-50">
                    <td className="px-4 py-2">{r.full_name || "—"}</td>
                    <td className="px-4 py-2">{r.national_id}</td>
                    <td className="px-4 py-2">{r.act_id || "—"}</td>
                    <td className="px-4 py-2">{r.amount} {r.currency || "EGP"}</td>
                    <td className="px-4 py-2">{fmtDateTime(r.submitted_at)}</td>
                    <td className="px-4 py-2">{r.submitted_by || "—"}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          STATUS_STYLES[r.status] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {r.asmaa_approved ? (
                        <span className="text-green-700 text-xs font-medium">
                          ✓ {r.asmaa_approved_by || "asmaa"}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {r.status === "approved" ? (
                        <span className="text-green-700 text-xs font-medium">✓ Approved</span>
                      ) : !r.asmaa_approved ? (
                        isApprover ? (
                          <button
                            onClick={() => handleApprove(r.id)}
                            disabled={approvingId === r.id}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-50"
                          >
                            {approvingId === r.id ? "Approving..." : "Approve"}
                          </button>
                        ) : (
                          <span className="text-gray-500 text-xs">Awaiting review</span>
                        )
                      ) : isManager ? (
                        <button
                          onClick={() => handleManagerApprove(r.id)}
                          disabled={managerApprovingId === r.id}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-50"
                        >
                          {managerApprovingId === r.id ? "Approving..." : "Manager Approve"}
                        </button>
                      ) : (
                        <span className="text-gray-500 text-xs">Awaiting manager approval</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Export Date Filter Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-60"
            onClick={() => !exporting && setShowExportModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-7 w-96 z-10">
            <h2 className="text-lg font-bold text-gray-800 mb-5 border-b pb-3">
              Export to Excel
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Filter by submitted date, or leave both blank to export every
              submission currently listed.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                From (Submitted Date)
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg p-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={exportDateFrom}
                onChange={(e) => setExportDateFrom(e.target.value)}
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                To (Submitted Date)
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg p-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={exportDateTo}
                onChange={(e) => setExportDateTo(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg
                           hover:bg-gray-200 text-sm font-medium"
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
              >
                Cancel
              </button>
              <button
                disabled={exporting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                           disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                onClick={handleExport}
              >
                {exporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
